import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class AthleteEditSanola extends HTMLElement {
  #activeTrigger = null;
  #submitting = false;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-athlete-look-open]');
    if (opener instanceof HTMLButtonElement) {
      this.#openLook(opener);
      return;
    }

    if (target.closest('[data-athlete-dialog-close]')) {
      this.#closeDialog();
      return;
    }

    const sizeButton = target.closest('[data-athlete-size]');
    if (sizeButton instanceof HTMLButtonElement) {
      this.#selectSize(sizeButton);
      return;
    }

    if (target.closest('[data-athlete-add]')) this.#addSelectedItems();
  };

  #handleChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-athlete-include]')) {
      const row = event.target.closest('[data-athlete-product]');
      row?.classList.toggle('is-excluded', !event.target.checked);
      const label = row?.querySelector('.athlete-edit-sanola__include-label');
      if (label) label.textContent = event.target.checked ? this.dataset.includedLabel || '' : this.dataset.includeLabel || '';
      this.#updateSummary();
    }
  };

  #openLook(trigger) {
    if (this.#submitting) return;

    const templateId = trigger.dataset.athleteLookOpen;
    const template = templateId ? this.querySelector(`#${CSS.escape(templateId)}`) : null;
    const content = this.querySelector('[data-athlete-dialog-content]');
    const dialogComponent = this.querySelector('dialog-component');
    if (!(template instanceof HTMLTemplateElement) || !content || !dialogComponent) return;

    content.replaceChildren(template.content.cloneNode(true));
    this.#activeTrigger = trigger;
    this.#updateSummary();
    dialogComponent.showDialog();
  }

  async #closeDialog() {
    const dialogComponent = this.querySelector('dialog-component');
    if (!dialogComponent) return;
    await dialogComponent.closeDialog();
    this.#activeTrigger?.focus();
  }

  #selectSize(button) {
    const row = button.closest('[data-athlete-product]');
    if (!row) return;

    row.querySelectorAll('[data-athlete-size]').forEach((option) => {
      option.setAttribute('aria-pressed', option === button ? 'true' : 'false');
    });
    row.dataset.variantId = button.dataset.variantId || '';
    row.dataset.price = button.dataset.price || '0';

    const price = row.querySelector('[data-athlete-price]');
    if (price) price.textContent = button.dataset.priceFormatted || '';
    const compareAt = row.querySelector('[data-athlete-compare-at]');
    if (compareAt) {
      compareAt.textContent = button.dataset.compareAtFormatted || '';
      compareAt.toggleAttribute('hidden', !button.dataset.compareAtFormatted);
    }

    this.#updateSummary();
  }

  #selectedRows() {
    return Array.from(this.querySelectorAll('[data-athlete-dialog-content] [data-athlete-product]')).filter((row) => {
      const input = row.querySelector('[data-athlete-include]');
      return input instanceof HTMLInputElement && input.checked && !input.disabled;
    });
  }

  #updateSummary() {
    const rows = this.#selectedRows();
    const availableRows = Array.from(
      this.querySelectorAll('[data-athlete-dialog-content] [data-athlete-include]:not(:disabled)')
    );
    const missing = rows.filter((row) => !row.dataset.variantId).length;
    const total = rows.reduce((sum, row) => sum + Number(row.dataset.price || 0), 0);
    const button = this.querySelector('[data-athlete-dialog-content] [data-athlete-add]');
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = this.#submitting || rows.length === 0 || missing > 0;
    if (this.#submitting) {
      button.textContent = this.dataset.addingLabel || '';
    } else if (rows.length === 0) {
      button.textContent = availableRows.length > 0 ? this.dataset.selectItemsLabel || '' : this.dataset.noItemsLabel || '';
    } else if (missing > 0) {
      const missingLabel = missing === 1 ? this.dataset.selectSizeLabel : this.dataset.selectSizesLabel;
      button.textContent = (missingLabel || '').replace('[count]', String(missing));
    } else {
      const itemLabel = rows.length === 1 ? this.dataset.addItemLabel : this.dataset.addItemsLabel;
      const label = (itemLabel || '').replace('[count]', String(rows.length));
      button.textContent = `${label} · ${this.#formatMoney(total)}`;
    }

    button.dataset.ready = missing === 0 && rows.length > 0 ? 'true' : 'false';
    button.toggleAttribute('aria-busy', this.#submitting);
  }

  #formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2)} ${this.dataset.currency || ''}`.trim();
    }
  }

  async #getCartItemCount(addedCount) {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Unable to read the updated cart');

      const cart = await response.json();
      const itemCount = Number(cart.item_count);
      if (!Number.isFinite(itemCount)) throw new Error('The updated cart count is invalid');

      return itemCount;
    } catch (error) {
      console.warn('Unable to read the updated cart count after adding the Athlete Edit selection.', error);
      const cartIcon = document.querySelector('cart-icon');
      const currentCount = Number(cartIcon?.currentCartCount);
      return (Number.isFinite(currentCount) ? currentCount : 0) + addedCount;
    }
  }

  async #addSelectedItems() {
    const button = this.querySelector('[data-athlete-dialog-content] [data-athlete-add]');
    if (!(button instanceof HTMLButtonElement) || button.dataset.ready !== 'true' || this.#submitting) return;

    const rows = this.#selectedRows();
    const items = rows.map((row) => ({ id: Number(row.dataset.variantId), quantity: 1 }));
    const error = this.querySelector('[data-athlete-dialog-content] [data-athlete-error]');
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((section) => section.dataset.sectionId)
      .filter(Boolean);

    this.#submitting = true;
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
    this.#updateSummary();

    const body = JSON.stringify({
      items,
      sections: [...new Set(sectionIds)].join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const responseText = await response.text();
      let data = {};

      if (responseText.trim()) {
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          throw new Error('The Athlete Edit cart response was not valid JSON', { cause: parseError });
        }
      }

      if (!response.ok || data.status || data.errors) throw new Error('Athlete Edit cart request failed');

      const itemCount = await this.#getCartItemCount(items.length);
      await this.#closeDialog();
      this.dispatchEvent(
        new CartAddEvent(data, this.id, {
          source: 'athlete-edit-sanola',
          itemCount,
          sections: data.sections,
        })
      );
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (
        cartDrawer &&
        !cartDrawer.hasAttribute('auto-open') &&
        'open' in cartDrawer &&
        typeof cartDrawer.open === 'function'
      ) {
        cartDrawer.open();
      }
    } catch (caught) {
      console.error('Unable to add the Athlete Edit selection to the cart.', caught);
      if (error) {
        error.textContent = this.dataset.errorLabel || '';
        error.hidden = false;
        error.focus();
      }
    } finally {
      this.#submitting = false;
      this.#updateSummary();
    }
  }
}

if (!customElements.get('athlete-edit-sanola')) {
  customElements.define('athlete-edit-sanola', AthleteEditSanola);
}
